import { Component, OnInit } from '@angular/core';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../../../core/auto-unsubscribe';
import { RequestTypeEnum } from '../../../shared/xatena-api/request-type';
import { RequestTypeBaseComponent } from '../request-type-base/request-type-base.component';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { DemandRequestDetailService } from '../demand-request-detail.service';
import { of } from 'rxjs';
import { RequestDto } from '../../../xatena-api';
import { FormlyModule } from '@ngx-formly/core';
import { AsyncPipe } from '@angular/common';

@AutoUnsubscribe()
@Component({
  selector: 'app-request-type-advanced-training',
  templateUrl: './request-type-advanced-training.component.html',
  styleUrls: ['./request-type-advanced-training.component.scss'],
  imports: [FormlyModule, AsyncPipe],
})
export class RequestTypeAdvancedTrainingComponent
  extends RequestTypeBaseComponent
  implements OnInit
{
  subs = new SubSink();

  constructor(
    protected translateService: TranslateService,
    demandRequestDetailService: DemandRequestDetailService
  ) {
    super(demandRequestDetailService);
  }

  public get staffEducationPrioritySelectList$() {
    const options: {
      key: number;
      value: string;
    }[] = [
      {
        key: 0,
        value: this.translateService.instant(
          'SHARED.STAFF_EDUCATION_PRIORITY_SELECT.HIGH'
        ),
      },
      {
        key: 1,
        value: this.translateService.instant(
          'SHARED.STAFF_EDUCATION_PRIORITY_SELECT.MID'
        ),
      },
      {
        key: 2,
        value: this.translateService.instant(
          'SHARED.STAFF_EDUCATION_PRIORITY_SELECT.LOW'
        ),
      },
    ];

    return of(options);
  }

  public get staffEducationDurationSelectList$() {
    const options: {
      key: number;
      value: string;
    }[] = [
      {
        key: 1,
        value: this.translateService.instant(
          'SHARED.STAFF_EDUCATION_DURATION_SELECT.ONE_YEAR'
        ),
      },
      {
        key: 2,
        value: this.translateService.instant(
          'SHARED.STAFF_EDUCATION_DURATION_SELECT.TWO_YEARS'
        ),
      },
      {
        key: 3,
        value: this.translateService.instant(
          'SHARED.STAFF_EDUCATION_DURATION_SELECT.THREE_YEARS'
        ),
      },
      {
        key: 4,
        value: this.translateService.instant(
          'SHARED.STAFF_EDUCATION_DURATION_SELECT.FOUR_YEARS'
        ),
      },
    ];

    return of(options);
  }

  protected updateTotalCost(request: RequestDto) {
    const yearCostTotal1 =
      +(request.yearCost1Course ?? 0) +
      +(request.yearCost1Housing ?? 0) +
      +(request.yearCost1Travel ?? 0) +
      +(request.yearCost1Food ?? 0);
    this.form.get('yearCostTotal1Course')?.setValue(yearCostTotal1, {
      emitEvent: false,
    });

    const yearCostTotal2 =
      +(request.yearCost2Course ?? 0) +
      +(request.yearCost2Housing ?? 0) +
      +(request.yearCost2Travel ?? 0) +
      +(request.yearCost2Food ?? 0);

    this.form.get('yearCostTotal2Travel')?.setValue(yearCostTotal2, {
      emitEvent: false,
    });

    const yearCostTotal3 =
      +(request.yearCost3Course ?? 0) +
      +(request.yearCost3Housing ?? 0) +
      +(request.yearCost3Travel ?? 0) +
      +(request.yearCost3Food ?? 0);

    this.form.get('yearCostTotal3Housing')?.setValue(yearCostTotal3, {
      emitEvent: false,
    });

    const yearCostTotal4 =
      +(request.yearCost4Course ?? 0) +
      +(request.yearCost4Housing ?? 0) +
      +(request.yearCost4Travel ?? 0) +
      +(request.yearCost4Food ?? 0);

    this.form.get('yearCostTotal4Food')?.setValue(yearCostTotal4, {
      emitEvent: false,
    });
  }

  ngOnInit(): void {
    this.subs.sink = this.form.valueChanges.subscribe(s => {
      this.updateTotalCost(s);
    });

    this.fields = [
      {
        hooks: {
          onInit: field => {
            this.subs.sink = this.isReadOnly$.subscribe(s => {
              if (s) {
                this.form.disable();
              } else {
                this.form.enable();
              }
            });
          },
        },
        hideExpression: model =>
          !(model.requestTypeId === RequestTypeEnum.AdvancedTraining),
        fieldGroup: [
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _(
                'DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_10_GRP_1.LABEL'
              ),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'staffEducationTopic',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_EDUCATION_TOPIC.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'staffEducationReason',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_EDUCATION_REASON.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'staffEducationPriority',
                type: 'select',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_EDUCATION_PRIORITY.LABEL'
                  ),
                  translate: true,
                  options: this.staffEducationPrioritySelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
              {
                key: 'staffEducationDuration',
                type: 'select',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_EDUCATION_DURATION.LABEL'
                  ),
                  translate: true,
                  options: this.staffEducationDurationSelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
            ],
          },
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _(
                'DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_10_GRP_2.LABEL'
              ),
              translate: true,
            },
            fieldGroupClassName: 'd-flex justify-content-around gap-3',
            fieldGroup: [
              {
                hideExpression: model => !(model.staffEducationDuration >= 1),
                fieldGroup: [
                  {
                    type: 'label',
                    className: 'year-label',
                    expressionProperties: {
                      'templateOptions.label': (model: any) => {
                        const staffEducationStartYear =
                          model?.staffEducationStartYear ??
                          new Date().getFullYear();

                        return staffEducationStartYear + 0;
                      },
                    },
                  },
                  {
                    key: 'yearCost1Course',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_ONE_COURSE.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost1Travel',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_ONE_TRAVEL.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost1Housing',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_ONE_HOUSING.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost1Food',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_ONE_FOOD.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCostTotal1Course',
                    type: 'currency',
                    templateOptions: {
                      readonly: true,
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_TOTAL_ONE.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                ],
              },
              {
                hideExpression: model => !(model.staffEducationDuration >= 2),
                fieldGroup: [
                  {
                    type: 'label',
                    className: 'year-label',
                    expressionProperties: {
                      'templateOptions.label': (model: any) => {
                        const staffEducationStartYear =
                          model?.staffEducationStartYear ??
                          new Date().getFullYear();

                        return staffEducationStartYear + 1;
                      },
                    },
                  },
                  {
                    key: 'yearCost2Course',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_TWO_COURSE.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost2Travel',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_TWO_TRAVEL.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost2Housing',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_TWO_HOUSING.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost2Food',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_TWO_FOOD.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCostTotal2Travel',
                    type: 'currency',
                    templateOptions: {
                      readonly: true,
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_TOTAL_TWO.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                ],
              },
              {
                hideExpression: model => !(model.staffEducationDuration >= 3),
                fieldGroup: [
                  {
                    type: 'label',
                    className: 'year-label',
                    expressionProperties: {
                      'templateOptions.label': (model: any) => {
                        const staffEducationStartYear =
                          model?.staffEducationStartYear ??
                          new Date().getFullYear();

                        return staffEducationStartYear + 2;
                      },
                    },
                  },
                  {
                    key: 'yearCost3Course',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_THREE_COURSE.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost3Travel',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_THREE_TRAVEL.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost3Housing',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_THREE_HOUSING.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost3Food',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_THREE_FOOD.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCostTotal3Housing',
                    type: 'currency',
                    templateOptions: {
                      readonly: true,
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_TOTAL_THREE.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                ],
              },
              {
                hideExpression: model => !(model.staffEducationDuration >= 4),
                fieldGroup: [
                  {
                    type: 'label',
                    className: 'year-label',
                    expressionProperties: {
                      'templateOptions.label': (model: any) => {
                        const staffEducationStartYear =
                          model?.staffEducationStartYear ??
                          new Date().getFullYear();

                        return staffEducationStartYear + 3;
                      },
                    },
                  },
                  {
                    key: 'yearCost4Course',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_FOUR_COURSE.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost4Travel',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_FOUR_TRAVEL.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost4Housing',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_FOUR_HOUSING.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCost4Food',
                    type: 'currency',
                    templateOptions: {
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_FOUR_FOOD.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                  {
                    key: 'yearCostTotal4Food',
                    type: 'currency',
                    templateOptions: {
                      readonly: true,
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.YEAR_COST_TOTAL_FOUR.LABEL'
                      ),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                ],
              },
            ],
          },
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _(
                'DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_10_GRP_3.LABEL'
              ),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'staffEducationComment',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_EDUCATION_COMMENT.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
            ],
          },
        ],
      },
    ];
  }
}
